import { useContext, useMemo, useState } from "react";
import { FiArrowLeft, FiCheck, FiLock, FiPackage } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import { money } from "./ProductCard";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";
import { productImageSrcSet } from "../utils/images";
import { findPromotion, promotionDiscount } from "../utils/promotions";
import { calculateStandardShipping } from "../utils/shipping";

const initialDetails = {
  email: "",
  firstName: "",
  lastName: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
  note: "",
  giftMessage: "",
};

const fieldLabels = {
  email: "Email address",
  firstName: "First name",
  lastName: "Last name",
  address: "Street address",
  city: "City",
  state: "State",
  postalCode: "ZIP code",
};

const shippingCountries = [
  "Australia", "Austria", "Belgium", "Canada", "Denmark", "Finland",
  "France", "Germany", "Hong Kong", "Ireland", "Italy", "Japan",
  "Luxembourg", "Malaysia", "Netherlands", "New Zealand", "Norway",
  "Portugal", "Singapore", "South Korea", "Spain", "Sweden",
  "Switzerland", "United Kingdom", "United States",
];

function CheckOut() {
  const { cartItems } = useContext(CartContext);
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [details, setDetails] = useState(initialDetails);
  const shippingMethod = "standard";
  const [packaging, setPackaging] = useState("standard");
  const [isGift, setIsGift] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const productsById = useMemo(
    () => new Map(productData.map((product) => [String(product.id), product])),
    []
  );

  const resolvedItems = cartItems
    .map((item) => {
      const product = productsById.get(String(item.id));
      const storedPrice = Number(item.price);
      const catalogPrice = Number(product?.price);

      return {
        ...item,
        product,
        quantity: Math.max(1, Number(item.quantity) || 1),
        unitPrice: Number.isFinite(storedPrice) ? storedPrice : catalogPrice,
      };
    })
    .filter((item) => item.product);

  const itemCount = resolvedItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const checkoutSubtotal = resolvedItems.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );
  const standardShipping = calculateStandardShipping({
    itemCount,
    subtotal: checkoutSubtotal,
  });
  const shippingCost = standardShipping;
  const effectivePackaging = isGift ? packaging : "standard";
  const packagingCost =
    effectivePackaging === "wood" ? 15 : effectivePackaging === "paper" ? 5 : 0;
  const promotion = findPromotion(location.state?.promotionCode);
  const discount = promotionDiscount(promotion, checkoutSubtotal);
  const orderTotal = checkoutSubtotal - discount + shippingCost + packagingCost;

  const updateDetail = (event) => {
    const { name, value } = event.target;
    setDetails((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = (currentStep) => {
    const fields = currentStep === 1
      ? ["email", "firstName", "lastName", "address", "city", "state", "postalCode"]
      : [];
    const nextErrors = {};

    fields.forEach((field) => {
      if (!details[field].trim()) nextErrors[field] = `${fieldLabels[field]} is required.`;
    });

    if (currentStep === 1 && details.email && !/^\S+@\S+\.\S+$/.test(details.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const continueTo = (nextStep) => {
    if (validate(step)) setStep(nextStep);
  };

  const placeOrder = () => {
    if (isSubmitting || !acceptedTerms) return;
    setIsSubmitting(true);
    const checkoutConfig = siteConfig.checkout || {};
    const orderPrefix = checkoutConfig.orderNumberPrefix || "ORDER";
    const orderNumber = `${orderPrefix}-${Date.now()}${Math.floor(100000 + Math.random() * 900000)}`;
    const order = {
      orderNumber,
      email: details.email,
      firstName: details.firstName,
      total: orderTotal,
      itemCount,
      promotionCode: discount > 0 ? promotion.code : null,
      expiresAt:
        Date.now() +
        Number(checkoutConfig.confirmationLifetimeMinutes || 30) * 60 * 1000,
    };
    try {
      window.sessionStorage.setItem(
        checkoutConfig.lastOrderStorageKey || "lastStorefrontOrder",
        JSON.stringify(order)
      );
    } catch {
      // The success screen still receives the order through router state.
    }
    window.setTimeout(() => {
      const origin = window.location.origin;
      const params = new URLSearchParams({
        orderNo: orderNumber,
        amount: orderTotal.toFixed(2),
        currency: siteConfig.shipping.currency || "USD",
        email: details.email,
        success_url: `${origin}/payment?state=success`,
        cancel_url: `${origin}/payment?state=cancel`,
        title: resolvedItems[0]?.product?.name || siteConfig.brandName,
      });
      window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
    }, 350);
  };

  if (!resolvedItems.length) {
    return (
      <main className="checkout-empty">
        <PageMeta title="Checkout" noIndex />
        <p className="eyebrow">Checkout</p>
        <h1>Your basket is waiting for something delicious.</h1>
        <p>Add a few pantry favorites before continuing to delivery.</p>
        <Link className="button button--primary" to="/shop">
          Shop pantry goods
        </Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <PageMeta title="Secure checkout" description={`Complete your ${siteConfig.brandName} pantry order.`} noIndex />
      <header className="checkout-header page-shell">
        <Link className="checkout-back" to="/cartpage">
          <FiArrowLeft aria-hidden="true" /> Back to cart
        </Link>
        <div>
          <p className="eyebrow">Secure checkout</p>
          <h1>Almost ready for the table.</h1>
        </div>
        <p className="checkout-secure"><FiLock aria-hidden="true" /> Encrypted</p>
      </header>

      <ol className="checkout-steps page-shell" aria-label="Checkout progress">
        {["Shipping", "Payment hand-off", "Review"].map((label, index) => {
          const number = index + 1;
          return (
            <li key={label} className={number === step ? "is-current" : number < step ? "is-complete" : ""}>
              <span>{number < step ? <FiCheck aria-hidden="true" /> : number}</span>
              <strong>{label}</strong>
            </li>
          );
        })}
      </ol>

      <div className="checkout-layout page-shell">
        <section className="checkout-panel">
          {step === 1 && (
            <div className="checkout-section">
              <div className="checkout-section__heading">
                <p className="eyebrow">Step 1 of 3</p>
                <h2>Where should we send it?</h2>
                <p>Checking out as a guest. You can create an account after ordering.</p>
              </div>

              <div className="checkout-fields">
                <Field label="Email address" name="email" type="email" autoComplete="email" value={details.email} error={errors.email} onChange={updateDetail} wide />
                <Field label="First name" name="firstName" autoComplete="given-name" value={details.firstName} error={errors.firstName} onChange={updateDetail} />
                <Field label="Last name" name="lastName" autoComplete="family-name" value={details.lastName} error={errors.lastName} onChange={updateDetail} />
                <Field label="Street address" name="address" autoComplete="street-address" value={details.address} error={errors.address} onChange={updateDetail} wide />
                <Field label="Apartment, suite, etc. (optional)" name="apartment" autoComplete="address-line2" value={details.apartment} onChange={updateDetail} wide />
                <Field label="City" name="city" autoComplete="address-level2" value={details.city} error={errors.city} onChange={updateDetail} />
                <Field label="State" name="state" autoComplete="address-level1" value={details.state} error={errors.state} onChange={updateDetail} />
                <Field label="ZIP code" name="postalCode" autoComplete="postal-code" value={details.postalCode} error={errors.postalCode} onChange={updateDetail} />
                <SelectField label="Shipping country" name="country" value={details.country} onChange={updateDetail} options={shippingCountries} wide />
              </div>

              <p className="checkout-inline-notice">
                Available destinations exclude countries and regions subject to applicable sanctions.
              </p>

              <fieldset className="checkout-options">
                <legend>Shipping method</legend>
                <OptionRadio name="shipping" value="standard" checked readOnly label="Standard shipping" note={siteConfig.shipping.deliveryWindow} price={standardShipping} />
              </fieldset>

              <label className="checkout-check">
                <input
                  type="checkbox"
                  checked={isGift}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setIsGift(checked);
                    if (!checked) setPackaging("standard");
                  }}
                />
                <span><strong>Is this a gift?</strong> Add a message and choose special packaging.</span>
              </label>

              {isGift && (
                <div className="checkout-gift">
                  <label htmlFor="giftMessage">Gift message (optional)</label>
                  <textarea id="giftMessage" name="giftMessage" rows="3" maxLength="240" value={details.giftMessage} onChange={updateDetail} />
                  <fieldset className="checkout-options checkout-options--compact">
                    <legend>Packaging</legend>
                    <OptionRadio name="packaging" value="standard" checked={packaging === "standard"} onChange={() => setPackaging("standard")} label="Simple recyclable packing" price={0} />
                    <OptionRadio name="packaging" value="paper" checked={packaging === "paper"} onChange={() => setPackaging("paper")} label="Gift box with paper fill" price={5} />
                    <OptionRadio name="packaging" value="wood" checked={packaging === "wood"} onChange={() => setPackaging("wood")} label="Keepsake wooden pantry box" price={15} />
                  </fieldset>
                </div>
              )}

              <label className="checkout-note" htmlFor="orderNote">Delivery note (optional)</label>
              <textarea id="orderNote" name="note" rows="3" value={details.note} onChange={updateDetail} />

              <button className="button button--primary checkout-next" type="button" onClick={() => continueTo(2)}>
                Continue to payment
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="checkout-section">
              <div className="checkout-section__heading">
                <p className="eyebrow">Step 2 of 3</p>
                <h2>Secure payment</h2>
                <p className="checkout-demo-note"><FiLock aria-hidden="true" /> Card details are collected securely by the payment provider after review.</p>
              </div>
              <div className="payment-card">
                <div className="payment-card__title">
                  <span>External secure checkout</span>
                  <span aria-label="Secure payment">SECURE</span>
                </div>
                <div className="checkout-fields">
                  <p>Your order, email, and total will be sent to our secure payment endpoint. No card number is collected on this page.</p>
                </div>
              </div>
              <div className="checkout-buttons">
                <button className="button button--secondary" type="button" onClick={() => setStep(1)}>Back</button>
                <button className="button button--primary" type="button" onClick={() => continueTo(3)}>Review order</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="checkout-section review-section">
              <div className="checkout-section__heading">
                <p className="eyebrow">Step 3 of 3</p>
                <h2>Review your order</h2>
                <p>Check the details below, then continue to secure payment.</p>
              </div>
              <ReviewBlock title="Deliver to" onEdit={() => setStep(1)}>
                <p>{details.firstName} {details.lastName}<br />{details.address}{details.apartment ? `, ${details.apartment}` : ""}<br />{details.city}, {details.state} {details.postalCode}</p>
              </ReviewBlock>
              <ReviewBlock title="Contact" onEdit={() => setStep(1)}><p>{details.email}</p></ReviewBlock>
              <ReviewBlock title="Shipping & packaging" onEdit={() => setStep(1)}><p>{shippingMethod === "express" ? "Express" : "Standard"} shipping<br />{effectivePackaging === "standard" ? "Recyclable packing" : effectivePackaging === "paper" ? "Gift box" : "Wooden pantry box"}</p></ReviewBlock>
              <ReviewBlock title="Payment" onEdit={() => setStep(2)}><p>Continue to secure payment after confirming this order.</p></ReviewBlock>
              <label className="review-consent"><input type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} /> <span>I agree to the <Link to="/terms" target="_blank" rel="noreferrer">terms</Link> and acknowledge the <Link to="/privacy" target="_blank" rel="noreferrer">privacy policy</Link> (each opens in a new tab).</span></label>
              <div className="checkout-buttons">
                <button className="button button--secondary" type="button" onClick={() => setStep(2)}>Back</button>
                <button className="button button--primary" type="button" disabled={isSubmitting || !acceptedTerms} onClick={placeOrder}>{isSubmitting ? "Redirecting…" : `Pay now · ${money.format(orderTotal)}`}</button>
              </div>
            </div>
          )}
        </section>

        <aside className="checkout-summary" aria-label="Order summary">
          <div className="checkout-summary__heading"><FiPackage aria-hidden="true" /><h2>Order summary</h2></div>
          <div className="checkout-summary__items">
            {resolvedItems.map((item) => (
              <div className="checkout-item" key={item.lineKey}>
                <div className="checkout-item__image">
                  <img
                    src={item.product.images[0]}
                    srcSet={productImageSrcSet(item.product.images[0])}
                    sizes="64px"
                    alt=""
                    width="1200"
                    height="1200"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>
                    {item.product.unit}
                    {item.variant && Object.values(item.variant).filter(Boolean).length > 0
                      ? ` · ${Object.values(item.variant).filter(Boolean).join(" · ")}`
                      : ""}
                  </p>
                </div>
                <strong>{money.format(item.unitPrice * item.quantity)}</strong>
              </div>
            ))}
          </div>
          <dl className="checkout-totals">
            <div><dt>Subtotal</dt><dd>{money.format(checkoutSubtotal)}</dd></div>
            {discount > 0 && <div><dt>Promo · {promotion.code}</dt><dd>−{money.format(discount)}</dd></div>}
            <div><dt>Shipping</dt><dd>{shippingCost === 0 ? "Free" : money.format(shippingCost)}</dd></div>
            {packagingCost > 0 && <div><dt>Gift packaging</dt><dd>{money.format(packagingCost)}</dd></div>}
            <div className="checkout-totals__total"><dt>Total <small>USD</small></dt><dd>{money.format(orderTotal)}</dd></div>
          </dl>
          <p className="checkout-summary__policy"><FiLock aria-hidden="true" /> Payment details are handled on the secure payment page.</p>
        </aside>
      </div>
    </main>
  );
}

function Field({ label, name, error, wide = false, ...props }) {
  return (
    <div className={`checkout-field ${wide ? "checkout-field--wide" : ""} ${error ? "has-error" : ""}`}>
      <label htmlFor={`checkout-${name}`}>{label}</label>
      <input id={`checkout-${name}`} name={name} aria-invalid={Boolean(error)} aria-describedby={error ? `${name}-error` : undefined} {...props} />
      {error && <span id={`${name}-error`} role="alert">{error}</span>}
    </div>
  );
}

function SelectField({ label, name, options, wide = false, ...props }) {
  return (
    <div className={`checkout-field ${wide ? "checkout-field--wide" : ""}`}>
      <label htmlFor={`checkout-${name}`}>{label}</label>
      <select id={`checkout-${name}`} name={name} {...props}>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </div>
  );
}

function OptionRadio({ label, note, price, ...props }) {
  return (
    <label className="checkout-radio">
      <input type="radio" {...props} />
      <span><strong>{label}</strong>{note && <small>{note}</small>}</span>
      <b>{price === 0 ? "Free" : money.format(price)}</b>
    </label>
  );
}

function ReviewBlock({ title, onEdit, children }) {
  return (
    <section className="review-block">
      <div><h3>{title}</h3>{children}</div>
      <button type="button" onClick={onEdit}>Edit</button>
    </section>
  );
}

export default CheckOut;
